import sample from 'utils/sample';
import uniq from 'utils/uniq';
import round from 'utils/round';
import { formatDate, parseDate, capitalizeString } from 'utils/format';
import { isUrlValid } from 'utils/validator';
import uuid from 'utils/uuid';

export const TEMPLATE_LAYOUT = {
  FIXED: 'fixed',
  PAGE: 'page',
  AUTOMATIC: 'automatic',
  SEQUENCE: 'sequence',
};

export const PLACEHOLDER_TYPE = {
  PHOTO: 'photo',
  MEDIA: 'media',
  TEXT: 'text',
  STATIC: 'static',
  VECTOR: 'vector',
};

export const PAGE_OPTION = {
  TOC: 'table-of-contents',
  PHOTO: 'photo',
  TIMELINE: 'personal-timeline',
  FAMILY_SHEET: 'family-sheet',
  FAMILY_TREE: 'family-tree',
  RECORD: 'record',
  TITLE: 'title',
  DEDICATION: 'dedication',
  DIVIDER: 'divider',
};

export const ORIENTATION = {
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape',
  SQUARE: 'square',
};

export const TREE_TYPE = {
  SINGLE: 'single',
  COMBO: 'combo',
  DESC: 'desc',
};

export const TREE_TYPE_ALIASES = {
  [TREE_TYPE.SINGLE]: ['single', 'std', 'standard'],
  [TREE_TYPE.COMBO]: ['combo', 'combination'],
  [TREE_TYPE.DESC]: ['desc', 'desccendants'],
};

/**
 * @param {object} template
 * @returns {string}
 */
export function getLayoutType(template) {
  return template.template.layout;
}

/**
 * @param {object} placeholder
 * @returns {boolean}
 */
export function isPhotoPlaceholder(placeholder = {}) {
  return (
    (placeholder.type === PLACEHOLDER_TYPE.PHOTO || placeholder.type === PLACEHOLDER_TYPE.MEDIA) &&
    placeholder.id !== 'background-placeholder'
  );
}

/**
 * @param {object} placeholder
 * @returns {boolean}
 */
export function isBackgroundPlaceholder(placeholder = {}) {
  return placeholder.type === PLACEHOLDER_TYPE.PHOTO && placeholder.id === 'background-placeholder';
}

/**
 * @param {object} placeholder
 * @returns {boolean}
 */
export function isStaticPlaceholder(placeholder = {}) {
  return placeholder.type === PLACEHOLDER_TYPE.STATIC;
}

/**
 * @param {object} placeholder
 * @returns {boolean}
 */
export function isVectorPlaceholder(placeholder = {}) {
  return placeholder.type === PLACEHOLDER_TYPE.VECTOR;
}

/**
 * @param {object} placeholder
 * @returns {boolean}
 */
export function isTextPlaceholder(placeholder = {}) {
  return placeholder.type === PLACEHOLDER_TYPE.TEXT;
}

export function getPhotoPlaceholders(page) {
  if (page.placeholders) {
    return page.placeholders.filter(isPhotoPlaceholder);
  }
  return [];
}

export function getTextPlaceholders(page) {
  if (page.placeholders) {
    return page.placeholders.filter(isTextPlaceholder);
  }
  return [];
}

export function getStaticPlaceholders(page) {
  if (page.placeholders) {
    return page.placeholders.filter(isStaticPlaceholder);
  }
  return [];
}

export function getVectorPlaceholders(page) {
  if (page.placeholders) {
    return page.placeholders.filter(isVectorPlaceholder);
  }
  return [];
}

/**
 * Returns current photo by specified config
 * @param {Array} photos
 * @param {object} params
 * @param {string} [params.key]
 * @param {number} [params.index]
 * @returns {object}
 */
export function getCurrentPhoto(photos = [], params = {}) {
  let photo;

  // first find photo
  if ('key' in params) {
    photo = photos.find(p => p.key === params.key);
  } else if ('index' in params) {
    if (params.index < photos.length) {
      photo = photos[params.index];
    }
  }

  // if photos exists, filter by tags
  if (photo) {
    if (photo.defaultValue) {
      return { photo, defaultValue: photo.defaultValue };
    }

    // if (params.forbiddenTags) {
    //   const isForbidden = params.forbiddenTags.every(tags =>
    //     tags.every(tag => photo.tags.indexOf(tag) >= 0),
    //   );
    //   if (isForbidden) {
    //     if (photo.defaultValue) {
    //       return { photo, defaultValue: photo.defaultValue };
    //     }
    //   }
    // }

    // if (params.allowedTags) {
    //   // TODO
    // }

    if (photo.alive === 1) {
      return { photo };
    }
    return { error: 'wrong uploaded photo(s)' };
  }

  return {};
}

export function getNumberOfCoverPhotos(template, coverId = '') {
  let numberOfCoverPhotos = 0;
  if (template.cover) {
    template.cover.forEach(page => {
      numberOfCoverPhotos = Math.max(numberOfCoverPhotos, getPhotoPlaceholders(page).length);
    });
  }
  return numberOfCoverPhotos;
}

export function getMinimumNumberOfPhotos(template, pageId = '') {
  const numberOfPhotos = () => {
    if (getLayoutType(template) === TEMPLATE_LAYOUT.PAGE && pageId.length > 0) {
      const page = template.pages.find(p => p.id === pageId);
      if (page) {
        return getPhotoPlaceholders(page).length;
      }
    }
    return template.template.minimumNumberOfPhotos || template.template.numberOfPhotos;
  };

  return numberOfPhotos() + (template.cover ? 1 : 0);
}

export function getMaximumNumberOfPhotos(template, pageId = '') {
  const numberOfPhotos = () => {
    if (getLayoutType(template) === TEMPLATE_LAYOUT.PAGE && pageId.length > 0) {
      const page = template.pages.find(p => p.id === pageId);
      if (page) {
        return getPhotoPlaceholders(page).length;
      }
    }
    return template.template.maximumNumberOfPhotos || template.template.numberOfPhotos;
  };

  return numberOfPhotos() + (template.cover ? 1 : 0);
}

export function getMinimumNumberOfPages(template, withCover = true) {
  let minimumNumberOfPages = template.template.minimumNumberOfPages;
  if (withCover && template.cover) {
    minimumNumberOfPages += 1;
  }
  return minimumNumberOfPages;
}

export function getMaximumNumberOfPages(template, withCover = true) {
  let maximumNumberOfPages = template.template.maximumNumberOfPages;
  if (withCover && template.cover) {
    maximumNumberOfPages += 1;
  }
  return maximumNumberOfPages;
}

export function getNumberOfAddablePages(template, numberOfPages) {
  return Math.max(0, getMaximumNumberOfPages(template) - numberOfPages);
}

export function getDisplayPageSize(template, section = 'pages') {
  if (template.template.displaySizes) {
    return template.template.displaySizes[section];
  }
  return template.template.displaySize;
}

export function getPageSize(template, section = 'pages', span = false) {
  if (template.template.size) {
    const [width, height] = template.template.size;
    return [span ? width * 2 : width, height];
  }

  if (template.template.sizes) {
    const [width, height] = template.template.sizes[section];
    return [span ? width * 2 : width, height];
  }

  throw new Error('The template does not have page size specified.');
}

export function getBleedZone(template, page, section = 'pages') {
  if (template.template.bleed) {
    let pageNumber = page.index;
    if (!template.cover && section === 'pages') {
      pageNumber = page.index + 1;
    }
    const type = pageNumber % 2 === 0 ? 'even' : 'odd';
    const bleedZoneKey = [`${section}:${type}`, section].find(key => template.template.bleed[key]);
    const bleedZone = template.template.bleed[bleedZoneKey];
    if (bleedZone && bleedZone.length === 4) {
      return bleedZone;
    }
  }

  return undefined;
}

export function getSafeZoneFrame(pageWidth, pageHeight, bleed) {
  if (!bleed) {
    return [0, 0, pageWidth, pageHeight];
  }

  const [left, top, right, bottom] = bleed;
  return [left, top, pageWidth - left - right, pageHeight - top - bottom];
}

export function isFrameInSafeZone(safeFrame, placeholderFrame, angle = 0, offsetX = 0) {
  if (!safeFrame || !placeholderFrame) {
    return true;
  }

  if (angle !== 0) {
    return true
  }

  const [safeX, safeY, safeWidth, safeHeight] = safeFrame.map(val => round(val, 4));
  const [placeholderX, placeholderY, placeholderWidth, placeholderHeight] = placeholderFrame.map(val => round(val, 4));

  if (placeholderX + offsetX < safeX || placeholderY < safeY) {
    return false;
  }

  if (placeholderX + offsetX + placeholderWidth > safeX + safeWidth) {
    return false;
  }

  if (placeholderY + placeholderHeight > safeY + safeHeight) {
    return false;
  }

  return true;
}

export function getTemplatePage(template, id, type = 'pages') {
  return template[type].find(p => p.id === id);
}

export function hasPageLayers(page) {
  if (page.placeholders) {
    return page.placeholders.some(placeholder => placeholder.layer);
  }
  return false;
}

/**
 * Transform page to common, more usable format with some usable data
 * @param {object} template
 * @param {object} page
 * @param {string} type
 * @param {object} options
 * @param {number} options.useServerAssets
 * @returns {object}
 */
export function getOriginalPage(template, page, type = 'pages', { useServerAssets } = {}) {
  let originalPage = {};
  if (template[type]) {
    originalPage = getTemplatePage(template, page.id, type);

    if (!originalPage && template[type].length === 1) {
      originalPage = template[type][0];
    }

    if (!originalPage) {
      originalPage = template[type].find(p => p.orientation === page.orientation);
    }

    if (!originalPage) {
      // TODO remove this and throw exception
      originalPage = template[type][0];
    }
  } else if (type === 'back') {
    // when template has no back section, create empty back page
    originalPage.id = 'back';
  }

  // Assign style to page
  originalPage = {
    ...getStyle(template, page.override?.styleName ?? originalPage.styleName),
    type: undefined,
    ...originalPage,
  };

  const isTOC = originalPage.optionId === PAGE_OPTION.TOC;

  let size = getPageSize(template, type);
  let useDisplaySize = false;
  if (!useServerAssets) {
    size = getDisplayPageSize(template, type) ?? size;
    useDisplaySize = true;
  }

  // DEFAULT STATE OF PAGE WITHOUT ORIENTATION
  let width = size[0];
  let height = size[1];

  if (originalPage.orientation === ORIENTATION.PORTRAIT) {
    width = Math.min(size[0], size[1]);
    height = Math.max(size[0], size[1]);
  }

  if (originalPage.orientation === ORIENTATION.LANDSCAPE) {
    width = Math.max(size[0], size[1]);
    height = Math.min(size[0], size[1]);
  }

  // BOOK
  if (type === 'pages' && originalPage.span === 1) {
    width *= 2;
  }

  // COVER
  let offsetX = 0;
  if (type === 'cover' && page.spine != null) {
    offsetX += page.spine;
    if (template.back) {
      const [backSizeWidth] = getPageSize(template, 'back');
      offsetX += backSizeWidth;
    }
    width += offsetX;
  }

  const bleed = getBleedZone(template, page, type);
  const safeFrame = getSafeZoneFrame(width, height, bleed);

  // placeholders defined in template
  // Assign style to all of placeholders
  const templatePlaceholders = (originalPage.placeholders ?? []).map(placeholder => ({
    ...getStyle(template, placeholder.styleName),
    ...placeholder,
  }));

  // placeholders defined in data
  const pagePlaceholders = [...(page.placeholders ?? [])];
  if (page.withTemplatePlaceholders) {
    templatePlaceholders.forEach(templatePlaceholder => {
      if (pagePlaceholders.find(p => p.id === templatePlaceholder.id) == null) {
        pagePlaceholders.push({
          id: templatePlaceholder.id,
          type: templatePlaceholder.type,
          data: {},
        });
      }
    });
  }

  const placeholders = [];

  if (page.override?.backgroundImage) {
    const url = page.override.backgroundImage;
    if (isUrlValid(url)) {
      placeholders.push({
        id: 'background-placeholder',
        type: PLACEHOLDER_TYPE.PHOTO,
        data: {
          photo_url: url,
          x: 0.5,
          y: 0.5,
          scale: 1,
          orientation: 0,
        },
        override: {
          id: 'background-placeholder',
          type: PLACEHOLDER_TYPE.PHOTO,
          frame: [0, 0, width, height],
        },
      });
    }
  }

  // TODO hack, when type is back, automatically use template defined placeholders
  if (hasPageLayers(originalPage) || type === 'back') {
    // when page has layers, use ONLY templatePlaceholders
    templatePlaceholders.forEach(placeholder => {
      const pagePlaceholder = pagePlaceholders.find(p => p.id === placeholder.id);
      placeholders.push({ data: {}, ...placeholder, ...pagePlaceholder });
    });
  } else {
    // page.placeholders cointains many other placeholders (etc. for leather cover titles)
    // so we need to filter only usable placeholders (defined in template or with full representation)
    pagePlaceholders.forEach(placeholder => {
      const templatePlaceholder = templatePlaceholders.find(p => p.id === placeholder.id);
      if (templatePlaceholder) {
        placeholders.push({ data: {}, ...templatePlaceholder, ...placeholder });
      } else if (placeholder.override) {
        const override = { type: placeholder.type, ...placeholder.override };
        // add only valid placeholders with full representation
        if (override.id && override.type && (override.frame || override.points)) {
          placeholders.push({ data: {}, ...placeholder, override });
        }
      }
    });
    // spine text placeholder
    if (type === 'cover' && page.spine != null) {
      let spineIndex = placeholders.findIndex(placeholder => placeholder.id === 'cover_spine_text');
      if (spineIndex < 0) {
        spineIndex =
          placeholders.push({
            id: 'cover_spine_text',
            type: 'text',
            data: {},
            override: {
              id: 'cover_spine_text',
              type: 'text',
              angle: -90,
              fontName: 'Baskerville-SemiBold',
              fontSize: 14,
              color: '#000000',
              alignment: 'center',
              verticalAlignment: 'center',
              label: 'SPINE TEXT',
            },
          }) - 1;
      }
      let spineY = height;
      let spineWidth = height;
      if (bleed != null) {
        spineY -= bleed[1];
        spineWidth -= bleed[1] + bleed[3];
      }
      placeholders[spineIndex].override.frame = [-page.spine, spineY, spineWidth, page.spine];
    }
  }

  return {
    key: uuid(),
    ...page,
    ...originalPage,
    placeholders,
    type,
    size: { width, height, offsetX, useDisplaySize },
    bleed,
    safeFrame,
    isTOC,
  };
}

export function getTransformedPage(template, originalPage, newPageId, type = 'pages') {
  const templatePage = getTemplatePage(template, newPageId, type);

  const placeholders = [];
  if (templatePage) {
    templatePage.placeholders.forEach(placeholder => {
      const { data } = originalPage.placeholders.find(p => p.id === placeholder.id) ?? {
        data: {},
      };

      placeholders.push({
        ...placeholder,
        data,
      });
    });
  }

  return getOriginalPage(
    template,
    {
      id: newPageId,
      placeholders,
    },
    type,
  );
}

export function getEmptyPage(template) {
  const size = getDisplayPageSize(template, 'pages') ?? getPageSize(template, 'pages');

  let width = size[0];
  let height = size[1];

  if (width < height) {
    width = size[0];
    height = size[1];
  }

  return { size: { width, height } };
}

export function hasSameOrientation(page, photo) {
  return (
    (page.orientation === ORIENTATION.PORTRAIT && photo.height >= photo.width) ||
    (page.orientation === ORIENTATION.LANDSCAPE && photo.height <= photo.width)
  );
}

export function hasPageOverlays(page) {
  if (page.overlays) {
    return page.overlays.length > 0;
  }
  return false;
}

export function getPageOverlay(page, index = 0) {
  return page.overlays[index];
}

export function isPageOverlayRequired(page) {
  if (hasPageOverlays(page)) {
    if (page.overlay_required) {
      if (page.overlay_required === true || page.overlay_required === 'true') {
        return true;
      }
    }
  }
  return false;
}

export function getAssetRootUrl(template) {
  return template.assetRootUrl ?? template.proxy.assetRootUrl;
}

export function getPageAssetUrl(
  template,
  pageId,
  options = { prefix: '', suffix: '', useServerAssets: false, folder: '/pages' },
) {
  if (getAssetRootUrl(template)) {
    return `${getAssetRootUrl(template)}/${options.useServerAssets ? 'assets_server' : 'assets_client'
      }${options.folder ?? '/pages'}/${options.prefix ?? ''}${pageId}${options.suffix ?? ''}.png`;
  }
  return '';
}

export function getAssetUrl(template, name) {
  if (getAssetRootUrl(template)) {
    return `${getAssetRootUrl(template)}${name}`;
  }
  return '';
}

export function getLayoutAssetUrl(
  template,
  pageId,
  options = { prefix: '', suffix: '', media: '' },
) {
  if (getAssetRootUrl(template)) {
    return `${getAssetRootUrl(template)}/assets_client/layouts/${options.prefix || ''}${pageId}${options.suffix || ''
      }${options.media || ''}.png`;
  }
  return '';
}

export function getInitialPageId(template, pages = []) {
  if (template.cover) {
    if (pages.length > 0) {
      return pages[0].id;
    }

    return template.cover[0].id;
  }

  if (pages.length === 1) {
    return pages[0].id;
  }

  if (getLayoutType(template) === TEMPLATE_LAYOUT.PAGE) {
    return template.pages[0].id;
  }

  if (getLayoutType(template) === TEMPLATE_LAYOUT.SEQUENCE) {
    const pageId = formatDate(new Date(), 'YYYY-MM');
    if (template.pages.find(page => page.id === pageId)) {
      return pageId;
    }
  }

  return '';
}

export function getPageByOptionId(template, optionId) {
  return template.pages.find(page => page.optionId === optionId);
}

export function getPageOptionIds(
  template,
  {
    withoutTOC,
    withoutRecords,
    withoutPhotos,
    withoutFamilyTree,
    withoutTitle,
    withoutDedication,
    withoutDivider,
  } = {},
) {
  const optionIds = [];

  template.pages.forEach(page => {
    if (page.optionId) {
      if (withoutTOC && page.optionId === PAGE_OPTION.TOC) {
        return;
      }
      if (withoutRecords && page.optionId === PAGE_OPTION.RECORD) {
        return;
      }
      if (withoutPhotos && page.optionId === PAGE_OPTION.PHOTO) {
        return;
      }
      if (withoutFamilyTree && page.optionId === PAGE_OPTION.FAMILY_TREE) {
        return;
      }
      if (withoutTitle && page.optionId === PAGE_OPTION.TITLE) {
        return;
      }
      if (withoutDedication && page.optionId === PAGE_OPTION.DEDICATION) {
        return;
      }
      if (withoutDivider && page.optionId === PAGE_OPTION.DIVIDER) {
        return;
      }
      optionIds.push(page.optionId);
    }
  });

  // TODO order
  // Offer TOC as a first option.
  return uniq(optionIds).sort(a => (a === PAGE_OPTION.TOC ? -1 : 0));
}

export function getPageOptionLabel(optionId = '') {
  const dictionary = {
    [PAGE_OPTION.TOC]: 'Table of Contents',
    [PAGE_OPTION.PHOTO]: 'Photos',
    [PAGE_OPTION.TIMELINE]: 'Person Timelines',
    [PAGE_OPTION.FAMILY_SHEET]: 'Family Group Sheets',
    [PAGE_OPTION.FAMILY_TREE]: 'Family Tree',
    [PAGE_OPTION.RECORD]: 'Record Pages',
    [PAGE_OPTION.TITLE]: 'Title Page',
    [PAGE_OPTION.DEDICATION]: 'Dedication Page',
    [PAGE_OPTION.DIVIDER]: 'Divider Page',
  };

  return dictionary[optionId] ?? optionId.split('-').join(' ');
}

export function getInitialCoverId(template, pages = []) {
  if (pages.length > 0) {
    return pages[0].id;
  }

  return template.cover[0].id;
}

export function getPageFilters(template) {
  return template.template.filters || [];
}

export function getNumberOfPages(template, pages) {
  let numberOfPages = 0;

  pages.forEach(page => {
    numberOfPages += 1;
    if (page.span) {
      numberOfPages += 1;
    }
  });

  return numberOfPages;
}

export function isNumberOfPagesValid(template, pages) {
  const numberOfPages = getNumberOfPages(template, pages);
  if (numberOfPages < getMinimumNumberOfPages(template)) {
    return false;
  }

  if (numberOfPages > getMaximumNumberOfPages(template)) {
    return false;
  }

  return true;
}

export function getOtherOrientation(page) {
  return page.orientation === ORIENTATION.PORTRAIT ? ORIENTATION.LANDSCAPE : ORIENTATION.PORTRAIT;
}

export function isChangeOrientationEnabled(template, page) {
  const originalPage = getOriginalPage(template, page);

  if (originalPage.orientation) {
    const otherOrientation = getOtherOrientation(originalPage);
    const otherOrientationPageId = originalPage[otherOrientation];

    if (template.pages.find(p => p.id === otherOrientationPageId)) {
      return true;
    }
  }

  return false;
}

export function isChangePageEnabled(template, pages = []) {
  return (
    getLayoutType(template) === TEMPLATE_LAYOUT.AUTOMATIC &&
    template.template.maximumNumberOfPages === 1 &&
    template.pages.length > 1 &&
    !isChangeOrientationEnabled(template, pages[0])
  );
}

export function isSelectInitialPageEnabled(template, renderCallback) {
  if (getLayoutType(template) === TEMPLATE_LAYOUT.SEQUENCE) {
    const actualMonth = formatDate(new Date(), 'YYYY-MM');

    let initialPages = [];
    template.pages.forEach(page => {
      if (page.id >= actualMonth) {
        initialPages.push({
          ...page,
          label: formatDate(parseDate(page.id, 'YYYY-MM'), 'MMMM YYYY'),
        });
      }
    });

    const minimumNumberOfPages = getMinimumNumberOfPages(template);
    initialPages = initialPages.slice(0, initialPages.length + 1 - minimumNumberOfPages);

    if (initialPages.length > 1) {
      return renderCallback(initialPages);
    }
  }

  return null;
}

export function getOtherOrientationPage(template, page) {
  let otherOrientationPage;

  const originalPage = getOriginalPage(template, page);

  if (originalPage.orientation) {
    const otherOrientation = getOtherOrientation(originalPage);
    const otherOrientationPageId = originalPage[otherOrientation];

    otherOrientationPage = template.pages.find(p => p.id === otherOrientationPageId);
  }

  return otherOrientationPage;
}

export function isChangePageOrientationEnabled(template, page) {
  return !!getOtherOrientationPage(template, page);
}

export function isRemovePageEnabled(template, pages = []) {
  return (
    getLayoutType(template) === TEMPLATE_LAYOUT.AUTOMATIC &&
    pages.length > getMinimumNumberOfPages(template) &&
    pages.length <= getMaximumNumberOfPages(template)
  );
}

export function isDuplicatePageEnabled(template, pages = []) {
  return (
    getLayoutType(template) === TEMPLATE_LAYOUT.AUTOMATIC &&
    pages.length < getMaximumNumberOfPages(template)
  );
}

export function isDuplicatePhotoEnabled(template, photos = []) {
  return photos.length < getMaximumNumberOfPhotos(template);
}

export function isTOCPage(template, pageId) {
  const templatePage = getTemplatePage(template, pageId, 'pages');
  if (templatePage) {
    return templatePage.optionId === PAGE_OPTION.TOC;
  }

  return false;
}

export function getPagesByOptions(template, optionIds) {
  return template.pages.filter(page => optionIds.indexOf(page.optionId) >= 0);
}

export function getTOCPages(template) {
  return getPagesByOptions(template, [PAGE_OPTION.TOC]);
}

export function getPageGroup(template, page) {
  const originalPage = getOriginalPage(template, page);

  return {
    name: originalPage.group || '',
    enabled: !!originalPage.group_default,
  };
}

export function getOtherPageOfGroup(template, page) {
  const pageGroup = getPageGroup(template, page);

  return template.pages.find(p => p.group === pageGroup.name && p.id !== page.id);
}

export function getPossiblePagesVariants(template, pages, renderCallback) {
  const variants = [];

  const pageGroup = getPageGroup(template, pages[0]);

  if (pageGroup.name.indexOf('borders') >= 0) {
    variants.push({
      type: 'BORDER',
      checked: pageGroup.enabled,
      title: 'INCLUDE WHITE BORDER',
      description: 'Toggle switch to add/remove the white border on your prints.',
    });
  }

  return variants.map(renderCallback);
}

export function getPagesFilterVariants(template, pages, renderCallback) {
  const filters = [];

  getPageFilters(template).forEach(filter => {
    filters.push({
      type: filter,
      checked: pages[0].filter === filter,
      title: 'Prefer Black & White?',
      description: 'Hit the switch to convert your print to B&W.',
    });
  });

  return filters.map(renderCallback);
}

export function getPossiblePages(template) {
  if (getLayoutType(template) === TEMPLATE_LAYOUT.AUTOMATIC) {
    return template.pages;
  }
  return [];
}

export function getPhotoOrientationType(photo) {
  const { width, height } = photo;

  if (width === height) {
    return ORIENTATION.SQUARE;
  }
  if (width > height) {
    return ORIENTATION.LANDSCAPE;
  }
  return ORIENTATION.PORTRAIT;
}

export function getPlaceholderOrientationType(placeholder) {
  const [, , width, height] = placeholder.frame;

  if (width === height) {
    return ORIENTATION.SQUARE;
  }
  if (width > height) {
    return ORIENTATION.LANDSCAPE;
  }
  return ORIENTATION.PORTRAIT;
}

export function getSuitablePage(pages, photo) {
  const suitablePages = pages
    .filter(p => {
      // Do not choose pages with text placeholder as default.
      return getPhotoPlaceholders(p).length === 1 && getTextPlaceholders(p).length === 0;
    })
    .filter(p => {
      return (
        getPlaceholderOrientationType(getPhotoPlaceholders(p)[0]) === getPhotoOrientationType(photo)
      );
    });

  let page = sample(suitablePages);

  if (!page) {
    page = sample(pages);
  }

  return page;
}

export function getBlankPage(pages) {
  const suitablePages = pages.filter(p => {
    return getPhotoPlaceholders(p).length === 0 && getTextPlaceholders(p).length === 0;
  });

  let page = sample(suitablePages);

  if (!page) {
    page = pages[0];
  }

  return page;
}

export function getSampleBookPage(template, type = 'pages', forbiddenPageIds = []) {
  const pages = template[type].filter(p => forbiddenPageIds.indexOf(p.id) < 0);

  if (pages.length > 0) {
    const suitablePages = pages.filter(p => {
      // Do not choose pages with text placeholder as default.
      return getPhotoPlaceholders(p).length === 1 && getTextPlaceholders(p).length === 0;
    });

    if (suitablePages.length > 0) {
      return sample(suitablePages);
    }
    return sample(pages);
  }

  return sample(template[type]);
}

export function getPlaceholder(template, pageId, placeholderId, type = 'pages') {
  const templatePage = getTemplatePage(template, pageId, type);

  if (!templatePage) {
    return null;
  }

  return templatePage.placeholders.find(placeholder => placeholder.id === placeholderId);
}

export function getStyle(template, styleName) {
  const { styles } = template.template;

  if (!styles || !styleName) {
    return {};
  }

  const [category, name] = styleName.split('.');
  if (!category || !name) {
    return {};
  }

  if (!styles[category]) {
    return {};
  }

  if (!styles[category][name]) {
    return {};
  }

  return styles[category][name];
}

export function getTemplateStyleCategories(template) {
  return Object.keys(template.template.styles ?? {});
}

export function getTemplateStyles(template, type, category = undefined) {
  const styleCategories = getTemplateStyleCategories(template);
  if (styleCategories.length < 1) {
    return [];
  }

  let categoryName;
  if (template.template.styles[category]) {
    categoryName = category;
  } else {
    categoryName = styleCategories[0];
  }

  const styles = Object.entries(template.template.styles[categoryName]).reduce(
    (acc, [key, style]) => {
      if (style.type === type) {
        acc.push({
          ...style,
          name: key,
          category: categoryName,
          label: capitalizeString(key.replace('_', ' ')),
        });
      }

      return acc;
    },
    [],
  );

  return styles;
}

/**
 * TEMP approach.
 *
 * Find the default border to use for the newly added photos.
 * This implementation is hotfix of the missing default style, which should contain that information.
 * The border is used from the first template photo placeholder of page, that has a border defined.
 * @param {object} template Parabo template data
 * @param {string} pageId PageId
 * @param {string} type pages | cover
 * @returns {object|null} Parabo border data if any was found.
 */
export function getDefaultPhotoBorder(template, pageId, type = 'pages') {
  const templatePage = getTemplatePage(template, pageId, type);

  if (!templatePage) {
    return undefined;
  }

  const borderedPhotoPlaceholder = templatePage.placeholders.find(
    placeholder => isPhotoPlaceholder(placeholder) && placeholder.border,
  );

  return borderedPhotoPlaceholder?.border;
}

export function getTreeTypeFromPage(page) {
  if (page.optionId !== PAGE_OPTION.FAMILY_TREE) {
    return null;
  }

  return Object.keys(TREE_TYPE_ALIASES).find(treeType =>
    TREE_TYPE_ALIASES[treeType].some(value => page.id.indexOf(value) >= 0),
  );
}

export function usePageThisTreeType(pageId, treeType) {
  let valid = false;
  if (TREE_TYPE_ALIASES[treeType]) {
    valid = TREE_TYPE_ALIASES[treeType].some(value => pageId.indexOf(value) >= 0);
  }
  return valid;
}

export function getFamilyTreeTypes(template) {
  const types = [];

  const getTypeFromAnyValue = value => {
    let type;
    Object.entries(TREE_TYPE_ALIASES).forEach(([key, values]) => {
      if (values.indexOf(value) >= 0) {
        type = key;
      }
    });
    return type;
  };

  // first search pages by optionId
  template.pages.forEach(page => {
    if (page.optionId === 'family-tree') {
      const value = page.id.split('-').pop();
      const type = getTypeFromAnyValue(value);
      if (type) {
        types.push(type);
      }
    }
  });

  // when no page has defined type, try sku
  if (types.length === 0) {
    if (template.id.indexOf('fh') === 0) {
      const value = template.id.split('-').pop();
      const type = getTypeFromAnyValue(value);
      if (type) {
        types.push(type);
      }
    }
  }

  return uniq(types);
}

export function getNumberOfGenerationsFromPage(page) {
  let value;

  page.id
    .split('-')
    .filter(part => part.endsWith('g'))
    .forEach(part => {
      try {
        const numberOfGenerations = parseInt(part.replace('g', ''), 10);
        if (numberOfGenerations > 0) {
          value = numberOfGenerations;
        }
      } catch (e) { }
    });

  return value;
}

export function getNumbersOfGenerations(template, suitableValues = [], type = undefined) {
  const numbersOfGenerations = [];

  if (template?.pages) {
    template.pages
      .filter(page => {
        if (type != null) {
          if (!usePageThisTreeType(page.id, type)) {
            return false;
          }
        }
        return true;
      })
      .forEach(page => {
        const numberOfGenerations = getNumberOfGenerationsFromPage(page);
        if (numberOfGenerations != null) {
          if (suitableValues.length > 0 && suitableValues.indexOf(numberOfGenerations) < 0) {
            // TODO when defined, allow only suitableValues
            return;
          }
          numbersOfGenerations.push(numberOfGenerations);
        }
      });
  } else {
    numbersOfGenerations.push(...suitableValues);
  }

  return uniq(numbersOfGenerations).sort();
}

export function getDefaultNumberOfGenerations(template, suitableValues = [], type = undefined) {
  const numbersOfGenerations = getNumbersOfGenerations(template, suitableValues, type);

  if (numbersOfGenerations.length > 0) {
    return numbersOfGenerations[0];
  }

  return undefined;
}

export function isValidDynamicPlaceholder(placeholder = {}) {
  const { id, type, override } = placeholder;
  if (!id || !type || !override) {
    return false;
  }
  if (id !== override.id || type !== override.type) {
    return false;
  }
  return true;
}

export function placeholderTextTransform(text = '', textTransform = 'none') {
  switch (textTransform) {
    case 'uppercase':
      return text.toUpperCase();
    case 'lowercase':
      return text.toLowerCase();
    case 'capitalize':
      return capitalizeString(text);
    default:
      return text;
  }
}

export function normalizeTextPlaceholderValue(text = '') {
  let value = text;
  if (value.indexOf('\t') >= 0) {
    value = value.replaceAll('\t', '    ');
  }
  return value;
}

export function getSpineSize(template, numberOfPages) {
  // TODO this can be removed when everything is ready!
  if (!template.id.startsWith('book-')) {
    return undefined;
  }

  if (template.proxy?.spine) {
    const minimumNumberOfPages = Object.keys(template.proxy.spine)
      .map(key => parseInt(key, 10))
      .sort((a, b) => b - a);

    if (minimumNumberOfPages.length > 0) {
      let key;
      if (numberOfPages < minimumNumberOfPages[minimumNumberOfPages.length - 1]) {
        key = minimumNumberOfPages[minimumNumberOfPages.length - 1];
      } else {
        key = minimumNumberOfPages.find(value => {
          return numberOfPages >= value;
        });
      }
      if (key) {
        const value = template.proxy.spine[key];
        if (value) {
          return parseFloat(template.proxy.spine[key]);
        }
      }
    }
  }
  return null;
}
